import { useFormContext } from 'react-hook-form';
import { AddressFields } from '@/components/forms/AddressFields';
import { HEALTH_BUSINESS_TYPE_OPTIONS, type HealthQuoteFormData } from '../../schema';
import { cn } from '@/utils';

/**
 * Step 1 of the Health branch: Business Type (New/Portability) + PIN Code.
 *
 * B6 (15 Sep QA): State and City used to be permanently `readOnly disabled`
 * boxes, auto-populated from the PIN lookup and nothing else -- so a PIN
 * code the lookup could not resolve left the broker stuck with two blanked,
 * unusable fields and no way to enter the location by hand at all. State is
 * now the shared `AddressFields` block: still auto-filled from
 * `geographyService.searchPincodes` (GET /geography/pincodes/search/?code=,
 * PincodeViewSet.search) on PIN blur, but State is a real dropdown the
 * broker can always pick from manually, and a failed/empty lookup leaves it
 * that way instead of dead-ending the step. `healthQuoteSchema`'s
 * `superRefine` still requires a non-blank State once the PIN is
 * syntactically valid -- only how it gets filled in has changed.
 */
export function ProductDetailsStep() {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<HealthQuoteFormData>();

  const businessType = watch('business_type');
  const pincode = watch('pincode');
  const state = watch('state');
  const city = watch('city');

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Product Details</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
          Tell us the business type and where the policy will be serviced.
        </p>

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Business Type
          <span className="text-red-500 ml-1">*</span>
        </label>
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          {HEALTH_BUSINESS_TYPE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors',
                businessType === option.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              )}
            >
              <input
                type="radio"
                value={option.value}
                checked={businessType === option.value}
                onChange={() =>
                  setValue('business_type', option.value, { shouldValidate: true, shouldDirty: true })
                }
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200">{option.label}</span>
            </label>
          ))}
        </div>
        {errors.business_type && (
          <p className="mb-6 -mt-4 text-sm text-red-600 dark:text-red-400">{errors.business_type.message}</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <AddressFields
            pincode={pincode || ''}
            onPincodeChange={(value) => setValue('pincode', value, { shouldValidate: true, shouldDirty: true })}
            pincodeRequired
            pincodeError={errors.pincode?.message}
            city={city || ''}
            onCityChange={(value) => setValue('city', value, { shouldValidate: false, shouldDirty: true })}
            cityError={errors.city?.message}
            state={state || ''}
            onStateChange={(value) => setValue('state', value, { shouldValidate: true, shouldDirty: true })}
            stateError={errors.state?.message}
          />
        </div>
      </div>
    </div>
  );
}
